"use client";

import { motion } from "framer-motion";
import {
  PlayCircle,
  Tags,
  Bug,
  Repeat,
  Shuffle,
  Archive,
} from "lucide-react";

/**
 * Sección educativa de la landing: enseña qué se puede hacer con Finbo
 * y aclara los conceptos clave (fijo vs variable, archivar vs eliminar).
 */

interface HowItWorksItem {
  icon: React.ReactNode;
  title: string;
  text: string;
}

const ITEMS: HowItWorksItem[] = [
  {
    icon: <PlayCircle size={22} />,
    title: "Así funciona",
    text: "Cargás tu ingreso del mes y tus gastos. Finbo te muestra cuánto te queda disponible y a dónde se va tu plata.",
  },
  {
    icon: <Tags size={22} />,
    title: "Organizá por categorías",
    text: "Agrupá tus gastos (Supermercado, Servicios, Tarjetas…) y marcá cada uno como pagado cuando lo abonás.",
  },
  {
    icon: <Bug size={22} />,
    title: "Cuidado con los gastos hormiga",
    text: "Ese café, el alfajor, el delivery… parecen poca plata pero sumados son un montón. Registralos y mirá la diferencia.",
  },
  {
    icon: <Repeat size={22} />,
    title: "Fijo vs variable",
    text: "Que cambie el precio no lo hace variable. FIJO es lo que pagás todos los meses sí o sí (alquiler, internet). VARIABLE es lo que algunos meses está y otros no (un regalo, una salida).",
  },
  {
    icon: <Shuffle size={22} />,
    title: "Gastos que cambian cada mes",
    text: "¿Este mes alfajores y el que viene helados? Creá una categoría «Antojos» y cargá cada cosa como gasto variable.",
  },
  {
    icon: <Archive size={22} />,
    title: "Archivar ≠ Eliminar",
    text: "Archivar saca el gasto de la lista pero queda en tu historial y no toca tu saldo. Eliminar lo borra de todos lados y corrige el saldo — solo para errores de carga.",
  },
];

const EASE = [0.16, 1, 0.3, 1] as const;

export default function HowItWorksSection() {
  return (
    <section
      id="que-podes-hacer"
      data-section="section-how-it-works"
      className="relative py-24 sm:py-36"
      aria-label="Qué podés hacer con Finbo"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div data-particle-shield className="mb-14 max-w-3xl">
          <div className="text-[12px] tracking-[0.2em] uppercase text-white/40 mb-5">
            Cómo se usa
          </div>
          <h2 className="text-[clamp(36px,5.5vw,72px)] font-extrabold leading-[1.05] tracking-tight">
            ¿Qué podés hacer{" "}
            <span className="gradient-text">con Finbo?</span>
          </h2>
          <p className="text-white/55 mt-5 text-lg sm:text-xl max-w-2xl leading-relaxed">
            Aprendé a llevar tus gastos y saber a dónde se te va la plata.
          </p>
        </div>

        <div
          data-particle-shield
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
        >
          {ITEMS.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.7, ease: EASE, delay: (i % 3) * 0.06 }}
              className="surface-paper rounded-2xl p-7 h-full flex flex-col"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-5">
                {item.icon}
              </div>
              <h3 className="text-white text-xl font-semibold mb-3 tracking-tight">
                {item.title}
              </h3>
              <p className="text-white/55 leading-relaxed text-[15px]">
                {item.text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
